import { XCircle } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ProductTableCell } from "@/components/shared/ProductTableCell";
import { formatEasternDateTime } from "@/utils";
import type { LandscapeLogEntry } from "@/types";
import { failedLandscapeReason, landscapeRunElapsedLabel, landscapeRunKeywordCountDisplay, runKindLabel } from "@/utils/landscapeRunStatus";
import { StatusIndicator } from "./StatusIndicator";

interface LandscapeLogDetailModalProps {
  log: LandscapeLogEntry | null;
  onClose: () => void;
}

export function LandscapeLogDetailModal({ log, onClose }: LandscapeLogDetailModalProps) {
  const failureReason = log?.status === "failed" ? failedLandscapeReason(log.error_detail) : null;
  return (
    <Dialog open={!!log} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="text-base">
            {log?.kind === "keyword_bank" ? "Keyword Bank Run Details" : "Landscape Run Details"}
          </DialogTitle>
        </DialogHeader>
        {log && (
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-4 pr-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">Product</p>
                  <div className="mt-1">
                    <ProductTableCell
                      name={log.product_name ?? log.hero_asin ?? "—"}
                      asin={log.hero_asin ?? undefined}
                      imageUrl={log.product_image_url ?? undefined}
                      wrapName
                      asinLinkToAmazon
                    />
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Brand</p>
                  <p className="text-sm text-foreground" data-testid="landscape-log-detail-brand">
                    {log.brand_name ?? "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Work</p>
                  <p className="text-xs font-medium text-foreground">{runKindLabel(log.kind)}</p>
                  {log.kind === "landscape" && (
                    <p className="text-xs text-muted-foreground">{log.search_phrase ?? "—"}</p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <div className="mt-0.5">
                    <StatusIndicator status={log.status} />
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Triggered By</p>
                  <p className="text-sm text-foreground" data-testid="landscape-log-detail-requested-by">
                    {log.requested_by_label || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Retries</p>
                  <p className="text-sm text-foreground">{log.retry_count}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Date Started</p>
                  <p className="text-sm text-foreground">
                    {log.requested_at ? formatEasternDateTime(log.requested_at) : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Duration</p>
                  <p className="text-sm text-foreground">
                    {landscapeRunElapsedLabel(
                      log.status,
                      log.started_at,
                      log.ended_at,
                      log.state_changed_at,
                      log.requested_at,
                      new Date(),
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Completed</p>
                  <p className="text-sm text-foreground">
                    {log.ended_at ? formatEasternDateTime(log.ended_at) : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Keywords</p>
                  <p className="text-sm text-foreground">
                    {landscapeRunKeywordCountDisplay(log.status, log.keyword_count)}
                  </p>
                </div>
              </div>

              {failureReason && (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertTitle>Failure Details</AlertTitle>
                  <AlertDescription className="mt-1 text-xs">{failureReason}</AlertDescription>
                </Alert>
              )}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
